<template>
    <UiDrawerPanel
        v-if="advisorShared && drawer.opened.value"
        class="advisor-drawer"
        :title="lang('advisor::advisor.title')"
        @close="drawer.close()"
        @keydown.esc="drawer.close()"
    >
        <div v-if="drawer.historyOpen.value" class="advisor-drawer__history">
            <div class="advisor-drawer__history-title">{{ lang('advisor::advisor.history.title') }}</div>
            <button type="button" class="advisor-drawer__chip" @click="startNew">
                {{ lang('advisor::advisor.history.new') }}
            </button>
            <p v-if="!drawer.loadingHistory.value && !drawer.conversations.value.length">
                {{ lang('advisor::advisor.history.empty') }}
            </p>
            <button
                v-for="conversation in drawer.conversations.value"
                :key="conversation.id"
                type="button"
                class="advisor-drawer__history-item"
                :class="{ 'advisor-drawer__history-item--current': conversation.id === drawer.conversationId.value }"
                @click="drawer.openConversation(conversation.id)"
            >
                <span class="advisor-drawer__history-item-title">{{ conversation.title }}</span>
                <span v-if="conversation.last_activity_at" class="advisor-drawer__history-item-date">
                    {{ formatActivity(conversation.last_activity_at) }}
                </span>
            </button>
        </div>

        <div v-else class="advisor-drawer__stream">
            <div v-if="!drawer.messages.value.length" class="advisor-drawer__welcome">
                <p>{{ lang('advisor::advisor.admin.welcome') }}</p>
                <div class="advisor-drawer__suggestions">
                    <button
                        v-for="suggestion in suggestions"
                        :key="suggestion"
                        type="button"
                        class="advisor-drawer__chip"
                        @click="drawer.send(suggestion)"
                    >
                        {{ suggestion }}
                    </button>
                </div>
            </div>

            <div
                v-for="(message, index) in drawer.messages.value"
                :key="index"
                class="advisor-drawer__message"
                :class="{
                    'advisor-drawer__message--user': message.role === 'user',
                    'advisor-drawer__message--assistant': message.role === 'assistant',
                }"
            >
                <!-- eslint-disable-next-line vue/no-v-html — reply_html is sanitized
                     server-side (Parsedown safe mode + purifier 'advisor' profile). -->
                <div v-if="message.role === 'assistant' && message.html" v-html="message.html" />
                <div v-else>{{ message.text }}</div>

                <template v-for="(entry, gi) in message.grounded ?? []" :key="gi">
                    <AdvisorVariantPicker
                        v-if="entry.kind === 'preview' && entry.variants?.length"
                        :g="entry"
                        :readonly="!!message.historical"
                        @cancel="dismiss(message, gi)"
                    />
                    <AdvisorPreviewCard
                        v-else-if="entry.kind === 'preview'"
                        :g="entry"
                        :readonly="!!message.historical"
                        @cancel="dismiss(message, gi)"
                    />
                    <AdvisorGrounded v-else :g="entry" />
                </template>
            </div>

            <div v-if="showTyping" class="advisor-drawer__message advisor-drawer__message--assistant">
                <span class="advisor-drawer__typing" role="status" :aria-label="lang('advisor::advisor.typing')">
                    <span class="advisor-drawer__typing-dot" />
                    <span class="advisor-drawer__typing-dot" />
                    <span class="advisor-drawer__typing-dot" />
                </span>
            </div>
        </div>

        <template #footer>
            <div class="flex gap-2">
                <Field
                    v-model="form.message"
                    size="sm"
                    label-position="none"
                    :field="{
                        name: `message`,
                        slug: `message`,
                        placeholder: lang('advisor::advisor.placeholder'),
                        type: 'text',
                        adminComponent: 'AdminText',
                    }"
                    @keyup.enter="submit"
                />
                <Button size="sm" :disabled="!form.message" :loading="drawer.busy.value" @click="submit">
                    {{ lang('advisor::advisor.send') }}
                </Button>
                <Button
                    v-if="drawer.messages.value.length"
                    v-tooltip="{
                        position: 'top',
                        message: lang('advisor::advisor.admin.reset'),
                    }"
                    size="sm"
                    variant="secondary"
                    @click="drawer.reset()"
                >
                    <icon icon="phosphor/regular/clock-clockwise" class="size-4"></icon>
                </Button>
                <Button
                    v-tooltip="{
                        position: 'top',
                        message: lang('advisor::advisor.history.title'),
                    }"
                    size="sm"
                    variant="secondary"
                    @click="drawer.toggleHistory()"
                >
                    <icon
                        icon="phosphor/regular/list-magnifying-glass

"
                        class="size-4"
                    ></icon>
                </Button>
            </div>
        </template>
    </UiDrawerPanel>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { lang } from '@/lang/composable';
import { useAdvisorDrawer } from '@src/Advisor/Pages/composables/useAdvisorDrawer';
import AdvisorGrounded from '@src/Advisor/Pages/AdvisorGrounded.vue';
import AdvisorPreviewCard from '@src/Advisor/Pages/AdvisorPreviewCard.vue';
import AdvisorVariantPicker from '@src/Advisor/Pages/AdvisorVariantPicker.vue';
import Field from '@src/Common/Components/Field.vue';
import type { DrawerMessage } from '@src/Advisor/Pages/types';

const drawer = useAdvisorDrawer();

const advisorShared = computed(() => usePage().props.advisor as { route?: string | null } | undefined);

const form = ref({
    message: '',
});

// Baked lang arrays are addressed by index; a missing key echoes back, which ends the scan.
const suggestions = computed(() => {
    const items: string[] = [];

    for (let i = 0; i < 6; i += 1) {
        const key = `advisor::advisor.admin.suggestions.${i}`;
        const value = lang(key);

        if (!value || value.endsWith(`suggestions.${i}`)) {
            break;
        }

        items.push(value);
    }

    return items;
});

const submit = () => {
    if (!form.value.message) return;

    drawer.send(form.value.message);
    form.value.message = '';
};

const startNew = (): void => {
    drawer.reset();
};

// Hide the typing indicator once streamed deltas start rendering text.
const showTyping = computed(() => {
    if (!drawer.busy.value) {
        return false;
    }

    const last = drawer.messages.value[drawer.messages.value.length - 1];

    return !(last && last.role === 'assistant' && last.streaming && last.text);
});

const formatActivity = (value: string): string => {
    const d = new Date(value);

    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' });
};

const dismiss = (message: DrawerMessage, index: number): void => {
    message.grounded?.splice(index, 1);
};
</script>

<style scoped>
.advisor-drawer__stream {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.advisor-drawer__welcome {
    font-size: 0.875rem;
    opacity: 0.85;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.advisor-drawer__suggestions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
}

.advisor-drawer__chip {
    border: 1px solid rgb(0 0 0 / 0.15);
    border-radius: 9999px;
    padding: 0.2rem 0.7rem;
    font-size: 0.8rem;
    cursor: pointer;
}

.advisor-drawer__chip:hover {
    background: rgb(0 0 0 / 0.05);
}

.advisor-drawer__message {
    padding: 0.5rem 0.75rem;
    border-radius: 0.5rem;
    max-width: 85%;
    font-size: 0.9rem;
}

.advisor-drawer__message--user {
    text-align: right;
    background: rgb(59 130 246 / 0.1);
    margin-left: auto;
}

.advisor-drawer__message--assistant {
    background: rgb(0 0 0 / 0.04);
    margin-right: auto;
    max-width: 100%;
}

.advisor-drawer__typing {
    display: inline-flex;
    gap: 0.3rem;
    padding: 0.2rem 0;
}

.advisor-drawer__typing-dot {
    width: 0.4rem;
    height: 0.4rem;
    border-radius: 9999px;
    background: currentColor;
    opacity: 0.4;
    animation: advisor-typing 1.2s ease-in-out infinite;
}

.advisor-drawer__typing-dot:nth-child(2) {
    animation-delay: 0.15s;
}

.advisor-drawer__typing-dot:nth-child(3) {
    animation-delay: 0.3s;
}

@keyframes advisor-typing {
    0%,
    60%,
    100% {
        opacity: 0.35;
        transform: translateY(0);
    }

    30% {
        opacity: 1;
        transform: translateY(-0.2rem);
    }
}

@media (prefers-reduced-motion: reduce) {
    .advisor-drawer__typing-dot {
        animation: none;
        opacity: 0.6;
    }
}

.advisor-drawer__history {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    font-size: 0.875rem;
}

.advisor-drawer__history-title {
    font-weight: 600;
}

.advisor-drawer__history-item {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    text-align: left;
    padding: 0.4rem 0.6rem;
    border: 1px solid rgb(0 0 0 / 0.1);
    border-radius: 0.5rem;
    cursor: pointer;
}

.advisor-drawer__history-item:hover {
    background: rgb(0 0 0 / 0.04);
}

.advisor-drawer__history-item--current {
    border-color: rgb(0 0 0 / 0.45);
}

.advisor-drawer__history-item-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.advisor-drawer__history-item-date {
    opacity: 0.6;
    white-space: nowrap;
}
</style>
